/**
 * Flow document shape — Cosmos `flows` container.
 *
 * Round-trips:
 *   - apps/public/src/types.ts -> FlowConfig (legacy in-bundle shape)
 *   - apps/public/src/config/flow-{pasen,office}.json (existing inputs to
 *     the migration script, Phase 9)
 *   - apps/admin/lib/mockData.ts -> FlowDoc (Phase 1 demo shape; superseded)
 *
 * Cosmos partition key: `/id`. One doc per flow.
 *
 * Status transitions (N6 state machine — review step removed):
 *   draft -> published    (admin/editor publishes directly)
 *   published -> draft     (admin/editor unpublishes / creates a draft)
 *   draft|published -> archived (admin archives; not deleted, just hidden)
 *   archived -> draft      (admin unarchives)
 *
 * Visibility (Slice 2 of the pivot, M+5.7):
 *   The state machine governs the editorial lifecycle; `visibility` is a
 *   parallel axis that controls public-landing surface presentation:
 *     - 'hidden'        — never shown to public, even in coming-soon list
 *     - 'coming-soon'   — appended to the public Coming Soon row
 *     - 'live'          — appended to the public main category carousel
 *   Existing docs without the field default to 'coming-soon' on read so
 *   the API never crashes on legacy data — the backfill script
 *   (`scripts/backfill-visibility.ts`) materializes the field for every
 *   doc in Cosmos exactly once.
 */

import { z } from 'zod';
import {
  CosmosBaseSchema,
  EntityIdSchema,
  IsoTimestampSchema,
  PublishStatusSchema,
  isHttpUrl,
} from './common.js';

// ---------------------------------------------------------------------------
// Tiered pricing (live in flow-*.json files)
// ---------------------------------------------------------------------------

/**
 * Quantity pricing matching the BrandBox Studio price sheet. Each value is the
 * per-unit price (EUR ex-VAT) for that band.
 *
 * Zero is a valid "band unpriced" sentinel (preserves the legacy JSON shape) —
 * `getTieredPrice` walks DOWN to the nearest priced band below it.
 *
 * The five original keys stay REQUIRED: every flow document in Cosmos carries
 * them, so requiring them breaks nothing and keeps the price sheet complete
 * for the bands the business has always priced. The two Chapter-10 upper bands
 * (`500-999`, `1000+`, F4) are OPTIONAL, which is what makes this a
 * no-migration change — a stored doc without them keeps validating and keeps
 * resolving exactly today's price.
 *
 * The key set and each key's required/optional-ness are pinned to
 * {@link TIER_BANDS} by `pricingBands.test.ts`. Add a band there and here, or
 * the parity test fails.
 *
 * NOTE (Chapter 10): `250+` is a legacy key name that now denotes the
 * **250-499** band. Never render it raw — use `describeBand`.
 */
export const TieredPricesSchema = z.object({
  '25-49': z.number().nonnegative(),
  '50-99': z.number().nonnegative(),
  '100-149': z.number().nonnegative(),
  '150-249': z.number().nonnegative(),
  '250+': z.number().nonnegative(),
  '500-999': z.number().nonnegative().optional(),
  '1000+': z.number().nonnegative().optional(),
});
export type TieredPrices = z.infer<typeof TieredPricesSchema>;

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export const OptionSchema = z
  .object({
    id: EntityIdSchema,
    label: z.string().min(1).max(256),
    description: z.string().max(2000).optional(),
    /** Price in EUR (ex-VAT). Single-tier fallback when tieredPrices absent. */
    price: z.number().nonnegative(),
    /** Alt label used by the "from €X" badge. */
    priceLabel: z.string().max(64).optional(),
    /** Path (relative) or absolute URL. Blob URLs are the prod case. */
    image: z.string().min(1).max(2048).optional(),
    /** Grouping label used by Spring "Essentials / Gadgets / Food..." UI. */
    category: z.string().max(128).optional(),
    /** Quantity-based pricing (overrides `price` once a tier matches). */
    tieredPrices: TieredPricesSchema.optional(),
    /** Minimum box count required to enable this option. */
    minBoxes: z.number().int().nonnegative().optional(),
    /**
     * Sort key for this option within its parent step. Lower values render
     * first. Phase K+4 introduces this so drag-to-reorder is unambiguous
     * regardless of array index — Cosmos's SDKs do not guarantee array order
     * preservation in every transport. Sparse spacing (multiples of
     * `ORDER_STEP`) lets callers insert between two options without
     * renumbering the entire list.
     *
     * Optional for backward compatibility: callers that read older flow docs
     * fall back to the array index (see `normalizeOptionOrder`).
     */
    order: z.number().int().nonnegative().optional(),
    /**
     * First-class "Other…" free-text option (N2). When `true`, selecting this
     * option reveals a free-text input on the public configurator so the
     * customer can describe a choice not in the list, and the typed value is
     * carried into the order summary / submission.
     *
     * This REPLACES the legacy magic convention where the public render keyed
     * the free-text slot off `option.id.toLowerCase().includes('other')`
     * (`ConfiguratorView.tsx`). The convention was undiscoverable in the admin
     * (an operator had to know to name the option's id `…-other`) and brittle
     * (any option whose id happened to contain "other" silently became a
     * free-text slot). The flag is explicit, editor-authored, and id-agnostic.
     *
     * Optional for backward compatibility: legacy docs without the field
     * validate; the public render falls back to the id convention only while
     * the part-2 generic render lands (see the N2a report's part-2 follow-up).
     */
    isOther: z.boolean().optional(),
    /**
     * Chapter 10 (F3) — the author's explicit declaration that this option's
     * unit price does NOT vary with quantity.
     *
     * Named after the FACT about the product, not after the gate that reads it:
     * a consumer asking "does ordering more make this cheaper?" gets its answer
     * from the field name alone, and the name stays true if the publish rule
     * ever changes. (`flatPrice` would read as a number next to `price`;
     * `flatPriceAcknowledged` names the ceremony rather than the product.)
     *
     * It is the escape hatch that keeps the publish gate passable
     * (`publishPricingGate.ts`): a priced option with no bands blocks publish
     * unless this is `true`. Free options (`price === 0`) never need it.
     *
     * Optional, and absent means "not declared" — a stored option without it
     * behaves exactly as before.
     */
    noQuantityDiscount: z.boolean().optional(),
    /**
     * Chapter 10 (F18) — an INTERNAL-ONLY link to the product this option was
     * sourced from. Asked for verbatim: *"I would love to have an area where I
     * can paste a product link, just internally visible. So I can always find
     * back what item we used."*
     *
     * Two properties are load-bearing, and both are enforced rather than
     * documented:
     *
     * 1. **Internal.** It is STRIPPED from the public CDN snapshot
     *    (`stripInternalOptionFields` in
     *    `packages/data-access/src/snapshot/buildContentSnapshot.ts`). A supplier
     *    URL in front of a customer is the opposite of what was requested — it
     *    exposes the sourcing. `buildContentSnapshot.test.ts` asserts its
     *    absence from the published payload.
     * 2. **http(s) only** (`isHttpUrl`). The admin renders the value as a
     *    clickable link for a colleague, so a stored `javascript:` / `data:` URL
     *    would be stored XSS aimed at our own team. Refused HERE, at the write
     *    boundary, so the API cannot be used to plant one; the drawer's input
     *    check is a convenience on top, never the control.
     *
     * Optional and additive — every option already in Cosmos keeps validating
     * against this `.strict()` schema with no migration, exactly as `minLeadDays`
     * and the two upper price bands did.
     */
    internalProductUrl: z
      .string()
      .min(1)
      .max(2048)
      .refine(isHttpUrl, {
        message:
          'internalProductUrl must be an absolute http(s) URL — javascript:, data:, relative paths and mailto: are rejected',
      })
      .optional(),
  })
  .strict();
export type Option = z.infer<typeof OptionSchema>;

/**
 * Spacing between option `order` values produced by `assignOptionOrder`.
 * Picked at 10 so an editor can drop a new option between two existing ones
 * without recomputing every neighbour's order.
 */
export const ORDER_STEP = 10;

/**
 * Stable-sort options by `order` (ascending). Options missing an explicit
 * `order` fall back to their original array index, preserving the legacy
 * "order = array index" semantics for any flow doc written before Phase K+4.
 *
 * Returns a new array — input is not mutated.
 */
export function sortOptionsByOrder<T extends Pick<Option, 'order'>>(
  options: readonly T[],
): T[] {
  return [...options]
    .map((opt, idx) => ({ opt, key: opt.order ?? idx, idx }))
    .sort((a, b) => {
      if (a.key !== b.key) return a.key - b.key;
      // Stable secondary sort by original index, so undefined-order options
      // keep their relative position.
      return a.idx - b.idx;
    })
    .map(({ opt }) => opt);
}

/**
 * Stamp every option with an explicit `order` based on its current index in
 * the array, spaced by `ORDER_STEP`. Used by the admin after a drag-reorder
 * so the PUT body unambiguously records the new ordering.
 *
 * Idempotent: calling twice produces the same result.
 */
export function assignOptionOrder<T extends Option>(
  options: readonly T[],
): T[] {
  return options.map((opt, idx) => ({ ...opt, order: (idx + 1) * ORDER_STEP }));
}

// ---------------------------------------------------------------------------
// Form fields (for `type: "form"` steps, e.g. the "Your Details" step)
// ---------------------------------------------------------------------------

export const FormFieldSchema = z
  .object({
    name: z.string().min(1).max(64),
    label: z.string().min(1).max(256),
    type: z.enum(['text', 'email', 'tel', 'textarea']),
    required: z.boolean(),
  })
  .strict();
export type FormField = z.infer<typeof FormFieldSchema>;

// ---------------------------------------------------------------------------
// Step dependencies (preserved from legacy FlowConfig)
// ---------------------------------------------------------------------------

export const DependencySchema = z
  .object({
    stepId: EntityIdSchema,
    allowedValues: z.array(z.string().min(1)).min(1),
  })
  .strict();
export type Dependency = z.infer<typeof DependencySchema>;

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export const StepTypeSchema = z.enum(['options', 'upload', 'form', 'summary']);
export type StepType = z.infer<typeof StepTypeSchema>;

export const StepSchema = z
  .object({
    id: EntityIdSchema,
    title: z.string().min(1).max(256),
    intro: z.string().max(2000).optional(),
    /** 0 = unlimited; 1 = single-choice; N = max-N. */
    maxSelections: z.number().int().nonnegative(),
    type: StepTypeSchema.optional(),
    dependsOn: z.array(DependencySchema).optional(),
    options: z.array(OptionSchema).optional(),
    /** Upload-step constraints. */
    accept: z.string().max(256).optional(),
    maxFileSizeMB: z.number().positive().max(100).optional(),
    /** Form-step fields. */
    fields: z.array(FormFieldSchema).optional(),
  })
  .strict();
export type Step = z.infer<typeof StepSchema>;

// ---------------------------------------------------------------------------
// Visibility (Slice 2 pivot)
// ---------------------------------------------------------------------------

/**
 * Public-landing visibility for a flow. Independent of `status`; an editor
 * picks this through the admin UI to control whether the flow surfaces on
 * the public landing and how:
 *
 *   - `'hidden'`       — entirely invisible to public callers
 *   - `'coming-soon'`  — appears in the public Coming Soon row (greyed pill,
 *                        not clickable)
 *   - `'live'`         — appears in the main category carousel (clickable)
 *
 * Field is `optional()` on the doc so legacy Cosmos rows without it still
 * validate; every read site coalesces with `?? 'coming-soon'`, matching
 * what the editor-facing default would have been. The backfill script
 * materializes the field on every doc so subsequent reads stop relying
 * on the coalesce-fallback.
 */
export const FlowVisibilitySchema = z.enum(['hidden', 'coming-soon', 'live']);
export type FlowVisibility = z.infer<typeof FlowVisibilitySchema>;

/**
 * The visibility a flow falls back to when its field is absent (legacy Cosmos
 * docs). The single source of the coalesce default that ~10 read sites used to
 * inline as `?? 'coming-soon'`.
 */
export const DEFAULT_FLOW_VISIBILITY: FlowVisibility = 'coming-soon';

/** Minimal shape the visibility helpers read — anything carrying the field. */
export type WithFlowVisibility = { readonly visibility?: FlowVisibility | null };

/**
 * The EFFECTIVE visibility of a flow, coalescing an absent field to
 * {@link DEFAULT_FLOW_VISIBILITY}. Every read site resolves through this rather
 * than coalescing inline, so the default lives in exactly one place.
 */
export function getVisibility(doc: WithFlowVisibility): FlowVisibility {
  return doc.visibility ?? DEFAULT_FLOW_VISIBILITY;
}

/** True when the flow is hidden from all public callers. */
export function isHidden(doc: WithFlowVisibility): boolean {
  return getVisibility(doc) === 'hidden';
}

/** True when the flow appears in the main category carousel (clickable). */
export function isLive(doc: WithFlowVisibility): boolean {
  return getVisibility(doc) === 'live';
}

/** True when the flow appears in the public Coming Soon row (greyed pill). */
export function isComingSoon(doc: WithFlowVisibility): boolean {
  return getVisibility(doc) === 'coming-soon';
}

/**
 * The Slice-2 public-read filter: a flow is publicly visible when its editorial
 * status is not `archived` AND its visibility is not `hidden`. This is the
 * SINGLE source of the public-visibility policy: both store adapters apply it
 * post-fetch (the InMemory and Cosmos adapters each fetch the candidate set
 * and filter through this predicate), so the rule is never re-encoded
 * elsewhere (e.g. as a Cosmos SQL filter) where it could silently drift.
 */
export function isPubliclyVisible(
  doc: WithFlowVisibility & { readonly status?: string },
): boolean {
  if (doc.status === 'archived') return false;
  return !isHidden(doc);
}

// ---------------------------------------------------------------------------
// Flow document
// ---------------------------------------------------------------------------

export const FlowDocSchema = CosmosBaseSchema.extend({
  docType: z.literal('flow'),
  title: z.string().min(1).max(256),
  intro: z.string().max(2000).optional(),
  status: PublishStatusSchema,
  /**
   * Public-landing visibility (Slice 2 pivot). Optional so existing
   * Cosmos docs without the field still validate; readers coalesce
   * with `?? 'coming-soon'`. The backfill script writes the field on
   * every doc so subsequent reads stop relying on the fallback.
   */
  visibility: FlowVisibilitySchema.optional(),
  /**
   * Box hero image — shown as the tile picture on the public landing
   * grid (M+5.5 Phase 3B). URL into the media Blob (`brandboxmedia<env>/
   * media/…`) or a same-origin `/api/media/local/…` path in dev. When
   * unset, the appended preview tile + the real customer-facing tile
   * (once the flow ships to the landing) fall back to a placeholder.
   * Additive optional field — existing docs in Cosmos keep working.
   */
  heroImage: z.string().url().optional().or(z.string().startsWith('/').optional()),
  /**
   * Minimum days between ordering and delivery for THIS box (Chapter 10, F8).
   *
   * Optional and additive: an existing Cosmos doc without the field still
   * validates, and readers fall back to the site-wide default and then to
   * `DEFAULT_MIN_LEAD_DAYS` (14) — see `resolveMinLeadDays` in `leadTime.ts`.
   * Unset is a legitimate state, so it is NOT a publish blocker.
   *
   * Capped at a year: a longer value is certainly a typo, and shipping it would
   * make the box unorderable with no obvious cause.
   */
  minLeadDays: z.number().int().positive().max(365).optional(),
  /**
   * The merch categories DECLARED for this box (Chapter 10, F17).
   *
   * Reported as *"in 'Categories for this box' I would like to delete a
   * category"*, and the missing button was the smaller half. The real defect was
   * that a category not yet assigned to an option had **nowhere on this document
   * to live**: the editor staged it in local state and deliberately did not
   * dirty the flow, because the PUT body could not carry it. So a category the
   * operator authored disappeared on the next reload, silently.
   *
   * This is the home. It is the authored DOMAIN of category names for the box —
   * a superset of the names currently assigned to options, so a category can
   * exist before any option uses it and can outlive the last option that did.
   * The admin unions this with the distinct `option.category` values to build
   * the manager's list, and the editor writes the union back on save, so the
   * field is self-healing on any box that predates it.
   *
   * The PUBLIC configurator still derives its merch tabs from the distinct
   * `option.category` values and must NOT read this field: a declared-but-unused
   * category would render as an empty tab.
   *
   * Optional and additive — every flow doc in Cosmos keeps validating against
   * this `.strict()` schema with no migration, as `minLeadDays` did. Each name is
   * bounded exactly like `Option.category` (the same string domain, since one is
   * assigned to the other) and the list is capped so a runaway client cannot
   * grow the document without limit.
   */
  categories: z.array(z.string().min(1).max(128)).max(64).optional(),
  /** Incrementing integer; bumped on every persisted change. */
  version: z.number().int().nonnegative(),
  /** Denormalized totals for dashboard rendering. */
  optionCount: z.number().int().nonnegative(),
  stepCount: z.number().int().nonnegative(),
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
  /** Email of the last writer (kept simple — full UserRef lives in audit). */
  updatedBy: z.string().email(),
  steps: z.array(StepSchema),
}).strict();
export type FlowDoc = z.infer<typeof FlowDocSchema>;

/**
 * N4a Fix #8 (GDPR) — PUBLIC-read projection of a flow doc. Identical to
 * `FlowDocSchema` except `updatedBy` (an editor email) is OPTIONAL, because
 * the public CDN snapshot STRIPS it (see
 * `packages/data-access/src/snapshot/buildContentSnapshot.ts`). Admin/Cosmos
 * read+write keep using the strict `FlowDocSchema`; only the public bundle
 * validates the PII-stripped snapshot against this. Extends the existing
 * schema (`.merge`) — not a fork.
 */
export const PublicFlowDocSchema = FlowDocSchema.merge(
  z.object({ updatedBy: z.string().email().optional() }),
).strict();
export type PublicFlowDoc = z.infer<typeof PublicFlowDocSchema>;

/** Lightweight projection returned by `GET /api/flows`. */
export type FlowSummary = Omit<FlowDoc, 'steps' | 'intro' | 'docType' | '_etag' | '_ts'>;

/**
 * N1b draft-visibility policy — public read teaser.
 *
 * The public read layer returns a flow's FULL content only when it is
 * `status: "published"`. A flow that is visible (`coming-soon` / `live`)
 * but NOT yet published (`draft`) must surface ONLY its tile
 * metadata so an in-progress box can render the Coming-Soon tile without
 * leaking its unreleased steps / options / pricing / intro copy
 * (N0 Bug-4: `GET /api/flows/winter-box` returned the full draft).
 *
 * `FlowDocSchema.steps` is required, so the teaser keeps `steps: []`
 * (a fresh empty array, never the input reference) and drops `intro`
 * entirely. Every other field is tile metadata the public landing already
 * consumes (`id`, `title`, `status`, `visibility`, `heroImage`, `version`,
 * `optionCount`, `stepCount`, timestamps). Admin reads are unaffected —
 * they call `*ForAdmin` and never go through this projection.
 */
export function toPublicFlowTeaser(flow: FlowDoc): FlowDoc {
  const teaser: FlowDoc = {
    ...flow,
    steps: [],
  };
  delete (teaser as { intro?: string }).intro;
  return teaser;
}

/**
 * Chapter 10 (F18) — per-OPTION fields that are INTERNAL authoring data and
 * must never reach a public reader.
 *
 * `internalProductUrl` is the supplier/product link an operator pastes so the
 * team can find the item again. Publishing it would tell every customer where
 * we source from — the exact opposite of the "just internally visible" the
 * field was asked for.
 *
 * Kept as a list so a future internal-only option field is one entry, not a
 * second walk. Add here; every public projection reads this one array.
 */
export const INTERNAL_OPTION_FIELDS = ['internalProductUrl'] as const;

/**
 * Public projection: a copy of `flow` with every {@link INTERNAL_OPTION_FIELDS}
 * entry removed from every option of every step.
 *
 * # Two callers, deliberately the same function
 *
 *   1. the publish-time CDN snapshot (`buildContentSnapshot`), and
 *   2. the LIVE `GET /api/flows/:id` when the caller holds no app role.
 *
 * They were separate concerns until P5a found that only (1) existed, which left
 * the field readable from the live route `gate.ts` keeps public. Two walks would
 * drift; this one cannot.
 *
 * Copies each level it rewrites (doc → steps → options) and never touches the
 * input: the STORED document keeps the operator's link, because that link IS
 * the feature. Only the projection loses it. Lives here beside
 * {@link toPublicFlowTeaser} because it is the same kind of thing — a rule
 * about what a public reader may see of a `FlowDoc`.
 */
export function stripInternalOptionFields(flow: FlowDoc): FlowDoc {
  return {
    ...flow,
    steps: flow.steps.map((step) => {
      if (!step.options) return step;
      return {
        ...step,
        options: step.options.map((option) => {
          const copy = { ...option } as Record<string, unknown>;
          for (const field of INTERNAL_OPTION_FIELDS) {
            delete copy[field];
          }
          return copy as typeof option;
        }),
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// Legacy <-> doc translation (used by Phase 9 migration script)
// ---------------------------------------------------------------------------

/**
 * Shape of the existing `flow-*.json` files in apps/public/src/config/. Kept
 * lenient (passthrough) because the legacy JSON has historical drift; we
 * tighten it at the FlowDoc boundary, not at the input boundary.
 */
export const FlowConfigSchema = z
  .object({
    flowId: z.string().min(1),
    title: z.string().min(1),
    intro: z.string().optional(),
    steps: z.array(z.unknown()),
  })
  .passthrough();
export type FlowConfig = z.infer<typeof FlowConfigSchema>;

/** Count options across all steps, treating missing arrays as zero. */
function countOptions(steps: unknown[]): number {
  return steps.reduce<number>((acc, raw) => {
    if (typeof raw !== 'object' || raw === null) return acc;
    const opts = (raw as { options?: unknown }).options;
    return acc + (Array.isArray(opts) ? opts.length : 0);
  }, 0);
}

export interface FlowConfigToDocOptions {
  /** Cosmos doc id (e.g. "spring", "office"). */
  id: string;
  /** Actor email recorded in `updatedBy`. */
  updatedBy: string;
  /** ISO timestamp to stamp both `createdAt` and `updatedAt`. */
  now: string;
  /** Optional initial status; defaults to "draft" so migration is non-destructive. */
  status?: 'draft' | 'published';
  /**
   * Optional initial visibility (Slice 2 pivot); defaults to 'coming-soon'.
   * Migration of pre-existing published flows passes `'live'` so the
   * backfill stays a no-op against the same dataset.
   */
  visibility?: FlowVisibility;
}

/**
 * Translate the existing flow JSON shape (FlowConfig) into a FlowDoc ready
 * for Cosmos upsert. Used by Phase 9's migration script.
 *
 * NOTE: This does NOT validate steps — the legacy JSON has known idiosyncrasies
 * (e.g. some options omit `description`). Validation happens after
 * translation when the doc is parsed against `FlowDocSchema`.
 */
export function flowConfigToDoc(
  legacy: FlowConfig,
  opts: FlowConfigToDocOptions,
): FlowDoc {
  const steps = (legacy.steps ?? []) as Step[];
  const stepCount = Array.isArray(steps) ? steps.length : 0;
  const optionCount = countOptions(steps);
  const doc: FlowDoc = {
    id: opts.id,
    docType: 'flow',
    title: legacy.title,
    ...(legacy.intro ? { intro: legacy.intro } : {}),
    status: opts.status ?? 'draft',
    visibility: opts.visibility ?? DEFAULT_FLOW_VISIBILITY,
    version: 1,
    optionCount,
    stepCount,
    createdAt: opts.now,
    updatedAt: opts.now,
    updatedBy: opts.updatedBy,
    steps,
  };
  return doc;
}
